import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Products from './products';
import Header from './header';
import Footer from './Footer';
import ProductModal from './ProductModal';
import styles from './Allproducts.module.css';

const Allproducts = ({ cartItems = 0, onAddToCart, cartList = [], cartTotal = 0, showCart = false, onCartToggle = () => {}, onRemoveItem = () => {} }) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    const [filterVisible, setFilterVisible] = useState(false);
    const filterRef = useRef(null);
    const filterToggleRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                filterRef.current && !filterRef.current.contains(event.target) &&
                filterToggleRef.current && !filterToggleRef.current.contains(event.target)
            ) {
                setFilterVisible(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const fetchAllProducts = async () => {
            setLoading(true);
            try {
                const response = await axios.get('/items');
                if (Array.isArray(response.data)) {
                    setProducts(response.data);
                } else {
                    console.error('Expected array but got:', response.data);
                    setProducts([]);
                }
            } catch (err) {
                setError(err.message);
                console.error('Error fetching all products:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchAllProducts();
    }, []);

    const handleItemClick = async (product) => {
        setModalLoading(true);
        try {
            const response = await axios.get(`/albums/${product.item_id}`);
            const photos = response.data.length > 0
                ? response.data.map(album => album.photo_url.startsWith('/') ? album.photo_url : '/' + album.photo_url)
                : [product.photo_url, product.photo_url, product.photo_url].map(url => url.startsWith('/') ? url : '/' + url);

            setSelectedProduct({ ...product, photos });
        } catch (error) {
            console.error('Error fetching album photos:', error);
            const photos = product.photos && product.photos.length > 0
                ? product.photos.map(url => url.startsWith('/') ? url : '/' + url)
                : [product.photo_url, product.photo_url, product.photo_url].map(url => url.startsWith('/') ? url : '/' + url);

            setSelectedProduct({ ...product, photos });
        } finally {
            setModalLoading(false);
        }
    };

    const closeModal = () => {
        setSelectedProduct(null);
    };

    // Filters for price and brand
    const [minPrice, setMinPrice] = React.useState(0);
    const [maxPrice, setMaxPrice] = React.useState(1000);
    const [maxAvailablePrice, setMaxAvailablePrice] = React.useState(1000);
    const [brands, setBrands] = React.useState([]);
    const [selectedBrands, setSelectedBrands] = React.useState([]);

    useEffect(() => {
        const applyFilters = () => {
            let filteredProducts = products;

            // Extract unique brands
            const uniqueBrands = [...new Set(products.map(p => p.brand).filter(Boolean))];
            setBrands(uniqueBrands);

            // Update maxAvailablePrice based on products
            const prices = products.map(p => p.sell_price);
            const maxPriceInData = prices.length > 0 ? Math.max(...prices) : 1000;
            setMaxAvailablePrice(maxPriceInData);

            // Clamp maxPrice if needed
            if (maxPrice > maxPriceInData) {
                setMaxPrice(maxPriceInData);
            }
            if (minPrice < 0) {
                setMinPrice(0);
            }

            if (minPrice !== '') {
                filteredProducts = filteredProducts.filter(p => p.sell_price >= minPrice);
            }
            if (maxPrice !== '') {
                filteredProducts = filteredProducts.filter(p => p.sell_price <= maxPrice);
            }
            if (selectedBrands.length > 0) {
                filteredProducts = filteredProducts.filter(p => selectedBrands.includes(p.brand));
            }

            setFilteredProducts(filteredProducts);
            setCurrentPage(1);
        };

        applyFilters();
    }, [products, minPrice, maxPrice, selectedBrands]);

    // State for filtered products
    const [filteredProducts, setFilteredProducts] = useState([]);

    const [currentPage, setCurrentPage] = React.useState(1);
    const itemsPerPage = 20;

    // Calculate total pages
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

    // Get products for current page
    const pagedProducts = Array.isArray(filteredProducts) ? filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage) : [];

    const goToPage = (page) => {
        if (page < 1) page = 1;
        else if (page > totalPages) page = totalPages;
        setCurrentPage(page);
    };

    return (
        <>
            <Header
                cartItems={cartItems}
                cartList={cartList}
                cartTotal={cartTotal}
                showCartDropdown={showCart}
                onCartToggle={onCartToggle}
                onRemoveItem={onRemoveItem}
            />


     <br/>     <br/> <br/> <br/> <br/><br/> <br/>
     


            <div className={styles.productsContainer}>
                <div className={styles.pageHeader}>
                    <h1 className={styles.pageTitle}>
                        All Products
                    </h1>
                </div>

                <button
                    ref={filterToggleRef}
                    onClick={() => setFilterVisible(!filterVisible)}
                    className={styles.filterToggle}
                >
                    {filterVisible ? 'Hide Filters' : 'Show Filters'}
                </button>

                <div className={styles.contentWrapper}>
                    <div
                        ref={filterRef}
                        className={`${styles.filterSidebar} ${!filterVisible ? styles.hidden : ''}`}
                    >
                    <div className={styles.filterSidebarBody}>
                        <div className={styles.filterSection}>
                            <h3 className={styles.filterTitle}>Filter by Price</h3>
                            <div className={styles.priceRangeContainer}>
                                <label htmlFor="minPrice" className={styles.priceLabel}>
                                    Min Price: <span className={styles.priceValue}>${minPrice}</span>
                                </label>
                                <input
                                    type="range"
                                    id="minPrice"
                                    min="0"
                                    max={maxAvailablePrice}
                                    value={minPrice}
                                    onChange={(e) => {
                                        const val = Math.min(Number(e.target.value), maxPrice);
                                        setMinPrice(val);
                                    }}
                                    className={styles.priceSlider}
                                />
                            </div>
                            <div className={styles.priceRangeContainer}>
                                <label htmlFor="maxPrice" className={styles.priceLabel}>
                                    Max Price: <span className={styles.priceValue}>${maxPrice}</span>
                                </label>
                                <input
                                    type="range"
                                    id="maxPrice"
                                    min="0"
                                    max={maxAvailablePrice}
                                    value={maxPrice}
                                    onChange={(e) => {
                                        const val = Math.max(Number(e.target.value), minPrice);
                                        setMaxPrice(val);
                                    }}
                                    className={styles.priceSlider}
                                />
                            </div>
                        </div>

                        <div className={styles.filterSection}>
                            <h3 className={styles.filterTitle}>Filter by Brand</h3>
                            <div className={styles.brandList}>
                                {brands.length === 0 && (
                                    <div className={styles.noProducts}>No brands available</div>
                                )}
                                {brands.map((brand) => (
                                    <div key={brand} className={styles.brandItem}>
                                        <input
                                            type="checkbox"
                                            id={`brand-${brand}`}
                                            checked={selectedBrands.includes(brand)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedBrands([...selectedBrands, brand]);
                                                } else {
                                                    setSelectedBrands(selectedBrands.filter(b => b !== brand));
                                                }
                                            }}
                                            className={styles.brandCheckbox}
                                        />
                                        <label htmlFor={`brand-${brand}`} className={styles.brandLabel}>
                                            {brand}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={() => setFilterVisible(false)}
                        className={styles.filterCloseBtn}
                    >
                        Close
                    </button>
                    </div>

                    <div className={styles.mainContent}>
                        {loading ? (
                            <div className={styles.loadingContainer}>Loading...</div>
                        ) : error ? (
                            <div className={styles.errorMessage}>{error}</div>
                        ) : pagedProducts.length === 0 ? (
                            <div className={styles.noProducts}>No products found.</div>
                        ) : (
                            <div className={styles.productsList}>
                                <Products
                                    products={pagedProducts}
                                    onAddToCart={onAddToCart}
                                    onItemClick={handleItemClick}
                                    cartList={cartList}
                                />
                            </div>
                        )}

                        {totalPages > 1 && (
                            <div className={styles.paginationContainer}>
                                <button 
                                    onClick={() => goToPage(currentPage - 1)} 
                                    disabled={currentPage === 1}
                                    className={styles.paginationButton}
                                >
                                    Previous
                                </button>
                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i + 1}
                                        onClick={() => goToPage(i + 1)}
                                        className={`${styles.paginationButton} ${currentPage === i + 1 ? styles.activePage : ''}`}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <button 
                                    onClick={() => goToPage(currentPage + 1)} 
                                    disabled={currentPage === totalPages}
                                    className={styles.paginationButton}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <br/>            <br/>
            <br/>
            <br/>
            <br/>
            <br/>
            

            <ProductModal 
                product={selectedProduct}
                onClose={closeModal}
                onAddToCart={onAddToCart}
            />
            
            <Footer />
        </>
    );
};

export default Allproducts;
